/**
 * Server clock estimation.
 *
 * Each server message that carries `server_time` gives a sample
 * `offset = serverTime - clientReceiveTime`. Network latency only ever makes the
 * sample *smaller* than the true offset, so the best estimate over a window is
 * the maximum of recent samples.
 */
export function offsetSample(serverTimeIso: string, receivedAtMs: number): number | null {
  const t = Date.parse(serverTimeIso);
  if (Number.isNaN(t)) return null;
  return t - receivedAtMs;
}

export class ServerClock {
  private samples: number[] = [];
  private readonly window: number;
  offset = 0;

  constructor(window = 8) {
    this.window = window;
  }

  sample(serverTimeIso: string, receivedAtMs: number = Date.now()): number {
    const s = offsetSample(serverTimeIso, receivedAtMs);
    if (s === null) return this.offset;
    this.samples.push(s);
    if (this.samples.length > this.window) this.samples.shift();
    this.offset = Math.max(...this.samples);
    return this.offset;
  }

  now(clientNowMs: number = Date.now()): number {
    return clientNowMs + this.offset;
  }

  reset(): void {
    this.samples = [];
    this.offset = 0;
  }
}

export const serverClock = new ServerClock();

/** Remaining ms until `deadlineIso`, measured on the server clock. Never negative. */
export function remainingMs(deadlineIso: string | null | undefined, serverNowMs: number): number {
  if (!deadlineIso) return 0;
  const d = Date.parse(deadlineIso);
  if (Number.isNaN(d)) return 0;
  return Math.max(0, d - serverNowMs);
}

/** Fraction (0..1) of time left given the total phase length. */
export function fractionLeft(remaining: number, totalMs: number): number {
  if (totalMs <= 0) return 0;
  return Math.min(1, Math.max(0, remaining / totalMs));
}

/** "0:09", "1:30", "1:02:05" — rounds UP so a timer never shows 0 while time remains. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/** Long-range countdown for events: "2d 4h", "3h 12m", "12m 05s", "45s". */
export function formatEventCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}
